import { type ElementType } from "react";
import { styled } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Skeleton from "@mui/material/Skeleton";
import Stack from "@mui/material/Stack";
import { BodyTextS, HeadingS } from "./Typography";

export const SidebarButton = styled(Button, {
    shouldForwardProp: (prop) => prop !== "isActive",
})<{ isActive?: boolean }>(({ theme, isActive }) =>
    theme.unstable_sx({
        display: "block",
        border: 0,
        outline: 0,
        pb: 4,
        borderBottom: "2px solid",
        borderBottomColor: isActive ? theme.palette.primary.main : "transparent",
        borderRadius: 0,
        color: isActive ? theme.palette.primary.main : "#404040",
        textAlign: "left",
        fontWeight: "normal",
        "&:hover": { backgroundColor: "transparent" },
        "&:focus": {
            outline: "none",
            boxShadow: "none",
        },
        "&:focus-visible": {
            outline: "none",
            boxShadow: "none",
            borderBottomColor: theme.palette.primary.main,
            color: theme.palette.primary.main,
        },
        "&:active": {
            outline: "none",
            boxShadow: "none",
        },
        "&.Mui-disabled": {
            color: theme.palette.grey.dark,
            opacity: 0.6,
            pointerEvents: "none",
        },
    }),
);

export const DetailLinkButton = styled(Box)<{ component?: ElementType; to?: string }>(({ theme }) =>
    theme.unstable_sx({
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText,
        borderRadius: 1,
        px: 4,
        py: 2,
        textDecoration: "none",
        fontFamily: "NNNittiGroteskTextBold",
        fontWeight: 600,
        fontSize: "1.8rem",
        minHeight: "4.4rem",
        whiteSpace: "nowrap",
    }),
);

export const SidebarHeading = styled(HeadingS, {
    shouldForwardProp: (prop) => prop !== "isActive",
})<{ isActive?: boolean }>(({ theme, isActive }) =>
    theme.unstable_sx({
        display: "flex",
        mb: 2,
        color: isActive ? theme.palette.primary.main : "#404040",
    }),
);

export const SidebarSubtitle = styled(BodyTextS, {
    shouldForwardProp: (prop) => prop !== "isActive",
})<{ isActive?: boolean }>(({ theme, isActive }) =>
    theme.unstable_sx({
        display: "block",
        mb: 0,
        color: isActive ? theme.palette.primary.main : "#404040",
    }),
);

export const SidebarAmount = styled(BodyTextS, {
    shouldForwardProp: (prop) => prop !== "isActive",
})<{ isActive?: boolean }>(({ theme, isActive }) =>
    theme.unstable_sx({
        display: "block",
        mb: 0,
        color: isActive ? theme.palette.primary.main : "#6D6D6D",
        fontFamily: '"NNNittiGroteskTextBold", sans-serif',
        fontWeight: 600,
        "& *": {
            color: "inherit",
            fontFamily: "inherit",
            fontWeight: "inherit",
        },
    }),
);

export const InlineSkeleton = styled(Skeleton)(({ theme }) =>
    theme.unstable_sx({
        display: "inline-block",
    }),
);

export const SidebarIcon = styled(Stack)(({ theme }) =>
    theme.unstable_sx({
        mr: 4,
        flexShrink: 0,
        "& svg": { width: "2.4rem", height: "2.4rem" },
    }),
);

export const CaretRightIcon = styled(Stack)(({ theme }) =>
    theme.unstable_sx({
        ml: 4,
        "& svg": { width: "0.8rem", height: "1.3rem" },
    }),
);

export const SidebarRow = styled(Stack)(({ theme }) =>
    theme.unstable_sx({
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "center",
    }),
);

export const SidebarContentRow = styled(Stack)(({ theme }) =>
    theme.unstable_sx({
        flexDirection: "row",
        alignItems: "flex-start",
    }),
);
